import { AssignStatus, PriorityFilterStatus } from "@/enum/list.enum";

import { ROUTE_CONFIG } from "@/routes/config";

/**
 * The minimal lead shape a per-lead Messages deep link needs. Kept structural rather than
 * `ListPerson` so the Contacts grid — whose row is a `ContactProspect` carrying a list membership
 * and a name, not a full lead — can ask the same question instead of hand-rolling the URL again
 * (PREC-4103).
 */
export interface LeadLinkSource {
	assignStatus?: AssignStatus;
	list?: { id: number; name?: string | null } | null;
	prospect?: { name?: string | null } | null;
}

/**
 * Build the Review-to-send URL scoped to a single lead: search by the prospect's name, pin the
 * lead's list, and widen the priority filter to ALL so a disqualified lead is not dropped. Returns
 * `undefined` when the prospect has no name to search by.
 *
 * **Not a general "review this lead" link** — call `resolveLeadReviewTarget` for that. Review-to-send
 * only ever queries `READY_TO_SEND_ASSIGN_STATUSES` (DRAFT / SAVED_FOR_LATER / ARCHIVED), and the
 * `assignStatus=["ALL"]` below expands to exactly those three, so this URL can only ever show a lead
 * still awaiting approval. Pointing a lead the campaign already owns at it is PREC-4103.
 */
export const buildReadyToSendHref = (row: LeadLinkSource): string | undefined => {
	const name = row.prospect?.name;

	if (!name) {
		return undefined;
	}

	const listIds = row.list
		? `&listIds=${encodeURIComponent(
				JSON.stringify([{ title: row.list.name, id: row.list.id, checked: true }])
			)}`
		: "";

	return (
		`${ROUTE_CONFIG.MESSAGE_CENTER}?searchValue=${encodeURIComponent(name)}` +
		`&priorityFilterStatus=${encodeURIComponent(JSON.stringify([PriorityFilterStatus.ALL]))}` +
		`&assignStatus=${encodeURIComponent(JSON.stringify(["ALL"]))}${listIds}`
	);
};
